import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  fetchRoadmapProjectFinalReviewConversation,
  sendRoadmapProjectFinalReviewConversationMessage,
} from "../../api/roadmapClient";
import type { RoadmapProjectFinalReviewConversationMessage } from "../../types/roadmapConversations";

interface ProjectFinalReviewConversationPanelProps {
  projectId: string;
}

export function ProjectFinalReviewConversationPanel({
  projectId,
}: ProjectFinalReviewConversationPanelProps): JSX.Element {
  const [messages, setMessages] = useState<RoadmapProjectFinalReviewConversationMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messageListRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setMessages([]);
    setInput("");
    setError(null);
    void loadConversation();
  }, [projectId]);

  const orderedMessages = useMemo(() => {
    return [...messages].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  }, [messages]);

  const loadConversation = async () => {
    if (!projectId) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchRoadmapProjectFinalReviewConversation(projectId);
      setMessages(result.messages);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load conversation";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!projectId || !input.trim()) return;
    setIsSending(true);
    setError(null);

    try {
      const result = await sendRoadmapProjectFinalReviewConversationMessage(projectId, input.trim());
      setMessages(result.messages.length ? result.messages : [result.message]);
      setInput("");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to send message";
      // eslint-disable-next-line no-console
      console.error("Final review conversation send failed", err);
      setError(message);
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    if (!messageListRef.current) return;
    messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
  }, [orderedMessages]);

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-lg border border-border bg-card shadow-soft">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <div>
            <div className="text-sm font-semibold text-foreground">Final review</div>
            <div className="text-xs text-muted-foreground">
              Validez les derniers ajustements et consolidez les retours.
            </div>
          </div>
          <button type="button" className="button button-ghost button-xs" onClick={() => void loadConversation()}>
            {isLoading ? "Chargement…" : "Rafraîchir"}
          </button>
        </div>

        <div ref={messageListRef} className="max-h-64 space-y-3 overflow-y-auto px-3 py-3">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Chargement de la conversation…</p>
          ) : orderedMessages.length === 0 ? (
            <p className="text-sm text-muted-foreground">Démarrez la discussion pour la revue finale.</p>
          ) : (
            orderedMessages.map((message) => (
              <div
                key={message.id}
                className={`flex flex-col gap-1 rounded border px-3 py-2 text-sm ${
                  message.role === "assistant"
                    ? "border-indigo-100 bg-indigo-50/60 text-foreground"
                    : "border-border bg-white text-foreground"
                }`}
              >
                <div className="flex items-center justify-between text-[11px] uppercase tracking-wide text-muted-foreground">
                  <span>{message.role}</span>
                  <span>{new Date(message.createdAt).toLocaleString()}</span>
                </div>
                <div className="whitespace-pre-line">{message.content}</div>
              </div>
            ))
          )}
        </div>

        <form className="border-t border-border bg-muted/40 px-3 py-3" onSubmit={handleSend}>
          <label className="flex flex-col gap-2 text-xs text-muted-foreground">
            Message
            <textarea
              className="input min-h-[90px]"
              placeholder="Partagez les ajustements ou questions finales"
              value={input}
              onChange={(event: any) => setInput(event.target.value)}
              disabled={isSending}
            />
          </label>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button type="submit" className="button button-primary" disabled={isSending || !input.trim()}>
              {isSending ? "Envoi…" : "Envoyer"}
            </button>
          </div>
        </form>
      </div>

      {error ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>
      ) : null}
    </div>
  );
}
